<template>
  <div class="slider-outer relative h-[100svh] w-full overflow-hidden bg-black">
    <transition-group name="mv-fade">
      <div v-for="(slide, index) in slides" v-show="currentSlide === index" :key="index" class="absolute h-[100svh] w-full">
        <!-- SP/PC 出し分け・最適化は OptimizedImage が担当（picture の media でブラウザが選択し、JS の isSP で src を差し替えない）。
             media の境界は pages/index.vue の preload と揃える（767px / 768px）。
             最初のスライド(LCP候補)を最優先で読み込み、残りは低優先度にして初期表示の帯域を譲る。 -->
        <OptimizedImage
          alt=""
          :class="['slider__img h-full w-full origin-left object-cover']"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          :pc-image-url="slide.pc"
          :pc-optimized-image-options="{ width: HERO_IMAGE_WIDTH_PC }"
          :preload="index === 0"
          :sp-image-url="slide.sp"
          :sp-optimized-image-options="{ width: HERO_IMAGE_WIDTH_SP }"
        />
      </div>
    </transition-group>
  </div>
</template>

<script lang="ts" setup>
import { getHeroSlidePath, HERO_SLIDE_COUNT, HERO_IMAGE_WIDTH_SP, HERO_IMAGE_WIDTH_PC } from '@/utils/wabunka/image/heroImage';
import { OptimizedImage } from '@/wabunka-src/common/components';

interface Slide {
  /** SP 用 生 URL（picture の source srcset） */
  sp: string;
  /** PC 用 生 URL（img の src。SP 非対応ブラウザのフォールバックも兼ねる） */
  pc: string;
}

// 静的 webp の生パスを OptimizedImage に渡し、Cloudflare 画像変換(format=auto)経由で帯域削減する。
// preload（pages/index.vue）と width を揃えるため、URL 生成元は heroImage.ts に集約している。
const slides: Slide[] = Array.from({ length: HERO_SLIDE_COUNT }, (_, index) => ({
  sp: getHeroSlidePath(index, true),
  pc: getHeroSlidePath(index, false),
}));

const currentSlide = ref(0);

// アンマウント後もタイマーが動いて currentSlide を更新し続けないよう、ID を保持して onBeforeUnmount でクリアする
let slideTimer: ReturnType<typeof setInterval> | undefined;
onMounted(async () => {
  await nextTick();
  slideTimer = setInterval(() => {
    currentSlide.value = (currentSlide.value + 1) % slides.length;
  }, 3500);
});

onBeforeUnmount(() => {
  if (slideTimer) clearInterval(slideTimer);
});
</script>

<style scoped lang="scss">
// 初期表示のフェードイン演出は削除（ヒーローを即時表示）。
// 旧実装は opacity:0 から held + 0.5s フェードでヒーローの表示を遅らせていた。
// 実測の結果フェードの有無/長さは LCP に影響しない（ヒーローはどのみち LCP 要素にならない）ため、
// 体感速度を優先してフェードなしにした。

// 左側へゆっくり移動させる（img は OptimizedImage 内部にあるため :deep で届かせる）
:deep(.slider__img) {
  animation: slide 4.5s linear forwards;
}

@keyframes slide {
  0% {
    transform: scale(1.04) translateX(0);
  }
  100% {
    transform: scale(1.04) translateX(-3%);
  }
}

// 画像をフェードイン・アウトさせる
.mv-fade-enter-active,
.mv-fade-leave-active {
  transition: opacity 1s ease;
}

.mv-fade-enter-from,
.mv-fade-leave-to {
  opacity: 0;
}
</style>
